import { LoyaltyRedemption } from './LoyaltyRedemption'
import { LoyaltyEnroll } from './LoyaltyEnroll'
import { Customer } from './CustomerInfo/CustomerInfo'
import { Cart } from '../../types/cart'
import React from 'react'
import { customerNotValidForLookup, useLoyalty } from '../../api/loyalty'
import { Fieldset } from '@local/do-secundo-fieldset'
import { Button } from '@toasttab/buffet-pui-buttons'
import { useCart } from '@local/do-secundo-cart-provider'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import { useFormikContext } from 'formik'
import { InfoTooltip, TooltipTextLayout } from '@toasttab/buffet-pui-tooltip'
import { t } from '@local/translations'
import { formatPhoneNumber } from '../../utils/phone-utils'

export const LoyaltyAccountLookup = ({
  cart,
  customer
}: {
  cart: Cart
  customer: Customer
}) => {
  const { restaurantInfo } = useRestaurant()

  const { optedInTo3pAccountLookup, setOptedInTo3pAccountLookup } = useCart()
  const { hasLoyaltyProgram, canLookupAccount, config, isLoyaltyLoading } =
    useLoyalty(customer, cart)

  const { setFieldValue } = useFormikContext()

  if (!hasLoyaltyProgram || !restaurantInfo?.name) {
    return null
  }

  if (optedInTo3pAccountLookup && !isLoyaltyLoading && !config?.enrolled) {
    return (
      <Fieldset
        label={t('loyalty-account-lookup.rewards-label')}
        collapsable
        collapsed={false}
      >
        <div className='py-2 space-y-1'>
          <TooltipTextLayout
            className='font-semibold text-error'
            helperIconButton={
              <InfoTooltip placement='auto'>
                <div className='type-subhead'>
                  {t('loyalty-account-lookup.email-label', {
                    email: customer.email
                  })}
                  <br />
                  {t('loyalty-account-lookup.phone-label', {
                    phone: formatPhoneNumber(
                      customer.phone,
                      customer.phoneCountryCode
                    )
                  })}
                </div>
              </InfoTooltip>
            }
          >
            {t('loyalty-account-lookup.no-account-found')}
          </TooltipTextLayout>
          <div className='type-subhead text-default'>
            {t('loyalty-account-lookup.confirm-details', {
              restaurantName: restaurantInfo.name
            })}
          </div>
        </div>
      </Fieldset>
    )
  }

  if (canLookupAccount) {
    return (
      <Fieldset
        label={t('loyalty-account-lookup.rewards-label')}
        collapsable
        collapsed={false}
      >
        <LoyaltyRedemption cart={cart} customer={customer} />
        <LoyaltyEnroll cart={cart} customer={customer} />
      </Fieldset>
    )
  }

  return (
    <Fieldset
      label={t('loyalty-account-lookup.rewards-label')}
      collapsable
      collapsed={false}
    >
      <div className='py-2'>
        <p className={'font-semibold text-default'}>
          {t('loyalty-account-lookup.get-rewarded')}
        </p>
        <p className={'type-subhead'}>
          {t('loyalty-account-lookup.lookup-description')}
        </p>
        <Button
          testId='lookup-loyalty-button'
          variant='secondary'
          onClick={() => {
            setFieldValue('optedInToLoyaltyLookup', true)
            setOptedInTo3pAccountLookup(true)
          }}
          className='my-4 min-w-52'
          disabled={customerNotValidForLookup(customer)}
        >
          {t('loyalty-account-lookup.lookup-button')}
        </Button>
        <p className={'type-caption'} data-testid='legal-links' role='group'>
          {t('loyalty-account-lookup.legal-text')}
        </p>
      </div>
    </Fieldset>
  )
}
